"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { useRef, useState, useEffect } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";

const projects = [
    {
        id: 1,
        title: "AR/VR/MR",
        image: "/images/ar-vr-mr-motor-telemetry.png",
        link: "/projects/ar-vr-mr"
    },
    {
        id: 2,
        title: "APP DEVELOPMENT",
        image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=2670&auto=format&fit=crop",
        link: "/projects/app-development"
    },
    {
        id: 3,
        title: "WEB DEVELOPMENT",
        image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=2672&auto=format&fit=crop",
        link: "/projects/web-development"
    },
    {
        id: 4,
        title: "UI/UX DESIGN",
        image: "/images/ui-ux-dark-wireframe-studio.png",
        link: "/projects/ui-ux-design"
    },
    {
        id: 5,
        title: "AI CONSULTANCY",
        image: "/aihero.jpg",
        link: "/projects/ai-consultancy"
    },
];

export default function Projects() {
    const scrollRef = useRef<HTMLDivElement>(null);

    // Drag to scroll state
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);

    const updateScrollButtons = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 10);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
            
            // Update active index for dots
            const index = Math.round(scrollLeft / (scrollRef.current.firstElementChild?.clientWidth || clientWidth));
            setActiveIndex(Math.min(index, projects.length - 1));
        }
    };

    useEffect(() => {
        window.addEventListener("resize", updateScrollButtons);
        updateScrollButtons();
        return () => window.removeEventListener("resize", updateScrollButtons);
    }, []);

    // Mouse Wheel Horizontal Scroll
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const handleWheel = (evt: WheelEvent) => {
            if (evt.deltaY === 0) return;

            // Check boundaries
            const isAtStart = container.scrollLeft === 0;
            const isAtEnd = Math.abs((container.scrollWidth - container.clientWidth) - container.scrollLeft) < 2;

            // If scrolling up (left) and not at start
            if (evt.deltaY < 0 && !isAtStart) {
                evt.preventDefault();
                container.scrollLeft += evt.deltaY;
            }
            // If scrolling down (right) and not at end
            else if (evt.deltaY > 0 && !isAtEnd) {
                evt.preventDefault();
                container.scrollLeft += evt.deltaY;
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, []);

    // Drag Handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
        setScrollLeft(scrollRef.current?.scrollLeft || 0);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - (scrollRef.current?.offsetLeft || 0);
        const walk = (x - startX) * 2; // Scroll-fast
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollLeft - walk;
        }
    };

    const scrollNext = () => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const itemWidth = container.firstElementChild?.clientWidth || container.clientWidth * 0.85;
            container.scrollBy({ left: itemWidth + 24, behavior: 'smooth' }); // 24 is gap-6
        }
    };

    const scrollPrev = () => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const itemWidth = container.firstElementChild?.clientWidth || container.clientWidth * 0.85;
            container.scrollBy({ left: -(itemWidth + 24), behavior: 'smooth' });
        }
    };

    const scrollToProject = (index: number) => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const childNode = container.children[index] as HTMLElement;
            if (childNode) {
                container.scrollTo({
                    left: childNode.offsetLeft - container.offsetLeft,
                    behavior: 'smooth'
                });
            }
        }
    };

    return (
        <section className="pt-0 pb-12 md:pb-16 bg-[#1C1C1C] text-white overflow-hidden relative group">

            {/* Floating Navigation Buttons (Hidden on small mobile if desired, but good for usability) */}
            <div className="absolute top-[45%] -translate-y-1/2 left-0 right-0 z-10 hidden md:flex justify-between px-6 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button
                    onClick={scrollPrev}
                    disabled={!canScrollLeft}
                    className={`w-14 h-14 rounded-full border border-white/20 bg-black/50 backdrop-blur-md flex items-center justify-center transition-all pointer-events-auto ${
                        canScrollLeft
                            ? "text-white hover:bg-white hover:text-black cursor-pointer shadow-lg"
                            : "opacity-0 cursor-default" // Hide completely when at start
                    }`}
                    aria-label="Previous project"
                >
                    <ChevronLeft size={28} />
                </button>
                <button
                    onClick={scrollNext}
                    disabled={!canScrollRight}
                    className={`w-14 h-14 rounded-full border border-white/20 bg-black/50 backdrop-blur-md flex items-center justify-center transition-all pointer-events-auto ${
                        canScrollRight
                            ? "text-white hover:bg-white hover:text-black cursor-pointer shadow-lg"
                            : "opacity-0 cursor-default" // Hide completely when at end
                    }`}
                    aria-label="Next project"
                >
                    <ChevronRight size={28} />
                </button>
            </div>

            <div
                ref={scrollRef}
                onScroll={updateScrollButtons}
                className={`flex overflow-x-auto gap-6 pl-6 md:pl-20 lg:pl-40 pr-6 md:pr-20 pb-8 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
            >
                {projects.map((project, index) => (
                    <motion.div
                        key={project.id}
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="relative flex-shrink-0 w-[85vw] md:w-[600px] aspect-[4/3] md:aspect-[16/10] snap-center group/card cursor-pointer overflow-hidden rounded-sm"
                    >
                        <Link href={project.link} className="block w-full h-full">
                            <Image
                                src={project.image}
                                alt=""
                                fill
                                sizes="(max-width: 768px) 85vw, 600px"
                                className="object-cover transition-transform duration-700 group-hover/card:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />

                            <div className="absolute bottom-0 left-0 p-6 md:p-8">
                                <h3 className="text-2xl md:text-3xl font-bold uppercase tracking-wide text-white">
                                    {project.title}
                                </h3>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>

            {/* Bottom Controls: Arrows + Dots for Mobile & Desktop */}
            <div className="flex justify-center items-center gap-6 mt-2 px-6">
                <button
                    onClick={scrollPrev}
                    disabled={!canScrollLeft}
                    className={`md:hidden w-10 h-10 rounded-full border border-white/20 flex items-center justify-center transition-all ${
                        canScrollLeft ? "text-white hover:bg-white/10" : "text-white/20"
                    }`}
                >
                    <ChevronLeft size={20} />
                </button>

                <div className="flex gap-2">
                    {projects.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => scrollToProject(idx)}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                activeIndex === idx ? "w-6 bg-white" : "w-2 bg-white/30 hover:bg-white/50"
                            }`}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>

                <button
                    onClick={scrollNext}
                    disabled={!canScrollRight}
                    className={`md:hidden w-10 h-10 rounded-full border border-white/20 flex items-center justify-center transition-all ${
                        canScrollRight ? "text-white hover:bg-white/10" : "text-white/20"
                    }`}
                >
                    <ChevronRight size={20} />
                </button>
            </div>
        </section>
    );
}
